import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const VERTICALES = ['Salud', 'Vivienda', 'Emprendimiento', 'Alimentación', 'Educación', 'Vestimenta'];
const SALUD_SUB = ['Salud: Farma', 'Salud: Consulta', 'Salud: Labs', 'Salud: QX', 'Salud: Integral'];

export default function CreateTanda() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    area: 'Salud',
    description: '',
    total_amount: '',
    contribution_amount: '',
    periodicity: 'Semanal',
    start_date: '',
    end_date: '',
    max_participants: 10,
    stripe_payment_link: ''
  });

  useEffect(() => {
    if (isEdit) fetchTanda();
  }, [id]);

  const fetchTanda = async () => {
    try {
      const { data } = await api.get(`/tandas/${id}`);
      setFormData({
        name: data.name || '',
        area: data.area || 'Salud',
        description: data.description || '',
        total_amount: String(data.total_amount ?? ''),
        contribution_amount: String(data.contribution_amount ?? ''),
        periodicity: data.periodicity || 'Semanal',
        start_date: data.start_date ? data.start_date.slice(0, 10) : '',
        end_date: data.end_date ? data.end_date.slice(0, 10) : '',
        max_participants: data.max_participants ?? 10,
        stripe_payment_link: data.stripe_payment_link || ''
      });
    } catch (error) {
      toast.error('Error al cargar la tanda');
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        contribution_amount: parseFloat(formData.contribution_amount),
        max_participants: parseInt(formData.max_participants),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        stripe_payment_link: formData.stripe_payment_link || null
      };

      if (isEdit) {
        await api.put(`/tandas/${id}`, payload);
        toast.success('Tanda actualizada correctamente.');
        navigate(`/tandas/${id}`);
      } else {
        await api.post('/tandas/', payload);
        toast.success('Tanda creada correctamente. Se enviaron notificaciones.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar la tanda: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card data-testid="tanda-form-card">
        <CardHeader>
          <CardTitle>{isEdit ? 'Editar Tanda' : 'Crear Nueva Tanda'}</CardTitle>
          {isEdit && <CardDescription>Modifica los datos de la tanda y guarda los cambios.</CardDescription>}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input data-testid="tanda-name-input" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Vertical</Label>
                <Select value={formData.area} onValueChange={v => setFormData({...formData, area: v})}>
                  <SelectTrigger data-testid="tanda-area-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Verticales</SelectLabel>
                      {VERTICALES.map(a => <SelectItem key={a} value={a} data-testid={`area-option-${a.toLowerCase()}`}>{a}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Salud (subcategorías)</SelectLabel>
                      {SALUD_SUB.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Otras</SelectLabel>
                      <SelectItem value="Otros">Otros</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea data-testid="tanda-description-input" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto Total a Recibir</Label>
                <Input data-testid="tanda-total-input" type="number" value={formData.total_amount} onChange={e => setFormData({...formData, total_amount: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Aportación por Periodo</Label>
                <Input data-testid="tanda-contribution-input" type="number" value={formData.contribution_amount} onChange={e => setFormData({...formData, contribution_amount: e.target.value})} required />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Periodicidad</Label>
                <Select value={formData.periodicity} onValueChange={v => setFormData({...formData, periodicity: v})}>
                  <SelectTrigger data-testid="tanda-periodicity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Semanal">Semanal</SelectItem>
                    <SelectItem value="Quincenal">Quincenal</SelectItem>
                    <SelectItem value="Mensual">Mensual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha de Inicio</Label>
                <Input data-testid="tanda-start-date-input" type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Término <span className="text-xs text-muted-foreground">(opcional)</span></Label>
                <Input data-testid="tanda-end-date-input" type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Máximo de Participantes</Label>
              <Input data-testid="tanda-max-participants-input" type="number" value={formData.max_participants} onChange={e => setFormData({...formData, max_participants: e.target.value})} required />
            </div>

            <div className="space-y-2">
              <Label>Liga de Pago Stripe (opcional)</Label>
              <Input
                data-testid="tanda-stripe-link-input"
                type="url"
                placeholder="https://buy.stripe.com/..."
                value={formData.stripe_payment_link}
                onChange={e => setFormData({...formData, stripe_payment_link: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">Liga personalizada de Stripe para que les participantes paguen su aportación en línea.</p>
            </div>

            <Button type="submit" data-testid="tanda-submit-btn" className="w-full mt-6" disabled={loading}>
              {loading ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Tanda'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
